import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useUpdateHistorySearch } from 'modules/shell/hooks';
import { SEARCH_BY, SEARCH_BY_FIELD, SEARCH_LEVEL } from 'constants/index';
import { phoneNumberFormatter } from 'utils';
import _isFunction from 'lodash/isFunction';

import { Icon, IconButton, ListItem, Typography } from '@material-ui/core';

import Drawer from 'sharedComponents/Drawer';
import Confirm from 'sharedComponents/Confirm';
import MainHeader from 'sharedComponents/MainHeader';
import List from 'sharedComponents/List';
import Button from 'sharedComponents/Button';
import { DropdownIcon, CloseIcon, AddClientIcon, DeleteIcon, ClearIcon, EmailIcon } from 'sharedComponents/Icons';
import Search from 'sharedComponents/SearchRM';
import Tabs from 'sharedComponents/Tabs';

import ClientInfiniteScrollList from '../ClientInfiniteScrollList';

const TABS = [
  {
    label: 'client list',
    disabled: false,
  },
  {
    label: 'all',
    disabled: false,
  },
];

const ClientSearchModalNew = ({
  classes,
  title,
  selectClient,
  customClientClickAction,
  allowClickOnIcon,
  selectedClients,
  setSelectedClients,
  searchLabel,
  isCentralSearch,
  goBack,
  groupId,
  withTabs,
  initSearch,
  isDefaultGroup,
  sendMailToSelectedClients,
  deleteFromGroup,
  titleWithSearch,
  showHeaderCloseIcon,
  fetchGroupClients,
  getGlobalClients,
  itemRenderer,
  contactType,
  renderSelectedItemsHeaderActions,
  handleAddClient,
  noRecordsInClientList,
  isBackOfHouseUser,
  hasCallInProgress,
  isMinimized,
  renderNonClientNumberLink,
  customOnCreateNewProfileHandler,
  customSearch,
  withAddClientButton,
  isEmailDefaultClientSearch,
}) => {
  const selectedCount = selectedClients.length;
  const history = useHistory();
  const searchParamsFromURL = new URLSearchParams(history.location.search);
  const searchLevel = isBackOfHouseUser
    ? SEARCH_LEVEL.GLOBAL
    : searchParamsFromURL.get('searchLevel') || SEARCH_LEVEL.CLIENT_LIST;
  const [tabValue, setTabValue] = React.useState(searchLevel);
  const [drawer, setDrawer] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [lastClientsModifications, setLastClientsModifications] = React.useState(null);
  const defaultSearchValue = isEmailDefaultClientSearch ? SEARCH_BY[0] : SEARCH_BY[2];
  const [searchBy, setSearchBy] = React.useState(
    searchParamsFromURL.has('field') ? searchParamsFromURL.get('field') : defaultSearchValue,
  );

  const [search, setSearch] = React.useState(searchParamsFromURL.get('q') || initSearch || '');
  const showRecentSearchResult = search.length === 0;
  const showTabs = withTabs && !showRecentSearchResult;
  const [, setQuery] = useUpdateHistorySearch({
    q: search,
    field: searchBy,
    searchLevel: tabValue,
    ...customSearch,
  });

  const fetch = (from, size, params = {}) => {
    if (groupId) {
      return fetchGroupClients(groupId, { from, size, ...params });
    }

    if (params.searchLevel === 'clientList') {
      return fetchGroupClients('clientList', {
        from,
        q: search,
        size,
        field: params.field,
      });
    }

    const shouldCheckClientListIfEmpty = withAddClientButton || searchBy === SEARCH_BY_FIELD.PHONE;

    return getGlobalClients(
      search,
      { from, size, searchField: params.field, includeClientList: true },
      shouldCheckClientListIfEmpty,
    );
  };

  const clearSelection = () => {
    setSelectedClients([]);
  };

  React.useEffect(() => {
    clearSelection();
    setQuery({
      q: search,
      field: searchBy,
      searchLevel: tabValue,
      ...customSearch,
    });
  }, [search, searchBy, tabValue]);

  const onDrawerClose = () => setDrawer(null);
  const onChangeSearchByHandler = () =>
    setDrawer({
      title: 'Search By',
      content: <SearchBy onClose={onDrawerClose} />,
    });

  const handleSearchByChange = (value) => {
    setSearchBy(value);
    onDrawerClose();
  };

  const onConfirmDelete = async () => {
    setConfirmDelete(false);
    setSelectedClients([]);

    const result = await deleteFromGroup(groupId, selectedClients);

    if (result === 'success') {
      setLastClientsModifications(Date.now());
    }
  };

  const onConfirmClose = () => {
    setConfirmDelete(false);
    setSelectedClients([]);
  };

  const handleDelete = () => setConfirmDelete(true);

  const SearchBy = () => (
    <List className={classes.list}>
      {SEARCH_BY.map((value) => (
        <ListItem
          key={value}
          onClick={() => handleSearchByChange(value)}
          className={classes.item}
          data-testid="btn-search-by"
          aria-label="btn-search-by"
        >
          {value}
        </ListItem>
      ))}
    </List>
  );

  const renderTitle = () => {
    return (
      <div className={classes.title}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="go back"
          data-testid="go-back"
          onClick={goBack || history.goBack}
          classes={{ root: classes.icon }}
        >
          <Icon component={CloseIcon} fontSize="small" />
        </IconButton>
        <div>{title}</div>
      </div>
    );
  };

  const getCloseIcon = () => {
    if (selectedCount) return ClearIcon;
    if (showHeaderCloseIcon) return CloseIcon;
    if (title) return null;
    return 'back';
  };

  const handleTabChange = (e, value) => {
    const tabValue = value ? SEARCH_LEVEL.GLOBAL : SEARCH_LEVEL.CLIENT_LIST;
    setTabValue(tabValue);
  };

  const renderPageHeaderForSelectedClientItems = () => (
    <>
      <IconButton onClick={sendMailToSelectedClients}>
        <EmailIcon color="inherit" className={classes.selectActionIcon} />
      </IconButton>
      {!isDefaultGroup && (
        <IconButton onClick={handleDelete} data-testid="group-clients-delete-client">
          <DeleteIcon color="inherit" className={classes.selectActionIcon} />
        </IconButton>
      )}
      <IconButton onClick={handleAddClient} data-testid="group-clients-add-client">
        <AddClientIcon color="inherit" className={classes.selectActionIcon} />
      </IconButton>
    </>
  );

  let pageTitle;
  if (selectedCount > 0) {
    pageTitle = <Typography className={classes.selected}>{selectedCount} selected</Typography>;
  }

  const backButtonCallback = () => {
    if (goBack) {
      setSearch('');
      return goBack();
    }

    return history.goBack();
  };

  const renderEmptySearchLink = () => {
    if (searchBy === SEARCH_BY_FIELD.PHONE && tabValue === SEARCH_LEVEL.GLOBAL && noRecordsInClientList) {
      const number = phoneNumberFormatter(search, 'outBoundVoice');

      if (number !== '') {
        return renderNonClientNumberLink(number);
      }
    }
  };

  const isSelectionEnabled = _isFunction(selectClient);

  const renderClientList = () => {
    return (
      <ClientInfiniteScrollList
        fetch={fetch}
        allowClickOnIcon={allowClickOnIcon}
        contactType={contactType}
        itemRenderer={itemRenderer}
        customClientClickAction={customClientClickAction}
        selectionEnabled={isSelectionEnabled}
        withTopExtraSpace={showTabs}
        useAsSearch
        lastExternalModifications={lastClientsModifications}
        selectClient={selectClient}
        selectedClients={selectedClients}
        search={search}
        searchField={searchBy}
        searchLevel={tabValue}
        displayAlphabet={false}
        renderEmptySearchLink={renderEmptySearchLink}
        customOnCreateNewProfileHandler={customOnCreateNewProfileHandler}
        showAddProfileButton={withAddClientButton && noRecordsInClientList}
      />
    );
  };

  return (
    <>
      <MainHeader
        title={pageTitle}
        closeIcon={getCloseIcon()}
        withTabs={showTabs}
        divider={!showTabs}
        classes={{ headerWithTabs: isCentralSearch ? classes.header : null }}
        titleWithSearch={titleWithSearch}
        customTitle={title ? renderTitle : null}
        onClose={selectedCount ? clearSelection : backButtonCallback}
        search={
          renderSelectedItemsHeaderActions && selectedCount
            ? null
            : () => {
                return (
                  <>
                    {searchLabel && <div className={classes.searchLabel}>{searchLabel}</div>}
                    <Search
                      placeholder="Search clients"
                      search={search}
                      applySearch={setSearch}
                      withDebounce
                      isHeaderWithin
                      hasClear
                      autoFocus
                    />
                    <Button
                      classes={{ root: classes.btnSelect }}
                      variant="outlined"
                      endIcon={<DropdownIcon />}
                      onClick={onChangeSearchByHandler}
                      data-testid="open-search-by"
                      aria-label="open-search-by"
                    >
                      {searchBy}
                    </Button>
                  </>
                );
              }
        }
      >
        {renderSelectedItemsHeaderActions && !!selectedCount && renderPageHeaderForSelectedClientItems()}
      </MainHeader>
      {showTabs && (
        <Tabs
          data-testid="tabs"
          tabs={TABS}
          onChange={handleTabChange}
          value={tabValue === SEARCH_LEVEL.CLIENT_LIST ? 0 : 1}
          classes={{
            appBarRootFixed: isCentralSearch ? classes.tabs : null,
            appBarRootFixedTopPadded:
              hasCallInProgress && isMinimized && isCentralSearch
                ? classes.tabsWithMinimizedCall
                : classes.tabsWithMinimizedCallInClients,
          }}
          isFixed
        />
      )}
      {renderClientList()}
      <Drawer title={drawer?.title} open={!!drawer} onClose={onDrawerClose} fullWidth={drawer?.fullWidth}>
        {drawer && drawer.content}
      </Drawer>
      <Confirm
        open={confirmDelete}
        title="Remove Person?"
        text={`Are you sure you want to remove ${
          selectedCount > 1 ? `these ${selectedCount} persons` : 'this person'
        }?`}
        label="Remove"
        onClose={onConfirmClose}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

ClientSearchModalNew.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  selectedClients: PropTypes.arrayOf(PropTypes.shape({ profileId: PropTypes.string })),
  title: PropTypes.string,
  searchLabel: PropTypes.string,
  isBackOfHouseUser: PropTypes.bool,
  handleAddClient: PropTypes.func,
  setSelectedClients: PropTypes.func,
  isCentralSearch: PropTypes.bool,
  renderSelectedItemsHeaderActions: PropTypes.bool,
  goBack: PropTypes.func,
  withTabs: PropTypes.bool,
  rootElementRef: PropTypes.shape({}),
  showHeaderCloseIcon: PropTypes.bool,
  noRecordsInClientList: PropTypes.bool,
  renderNonClientNumberLink: PropTypes.func,
  customOnCreateNewProfileHandler: PropTypes.func,
  customSearch: PropTypes.shape({}),
};

ClientSearchModalNew.defaultProps = {
  classes: {},
  selectedClients: [],
  title: '',
  handleAddClient: () => null,
  renderSelectedItemsHeaderActions: true,
  searchLabel: '',
  isCentralSearch: false,
  goBack: null,
  withTabs: false,
  rootElementRef: null,
  setSelectedClients: () => null,
  renderNonClientNumberLink: () => null,
  customOnCreateNewProfileHandler: () => null,
  customSearch: {},
};

export default ClientSearchModalNew;
